import React from 'react'
import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import CallEndIcon from '@mui/icons-material/CallEnd'
import MicIcon from '@mui/icons-material/Mic'
import MicOffIcon from '@mui/icons-material/MicOff'
import PresentToAllIcon from '@mui/icons-material/PresentToAll'
import StopScreenShareIcon from '@mui/icons-material/StopScreenShare'
import VideocamIcon from '@mui/icons-material/Videocam'
import VideocamOffIcon from '@mui/icons-material/VideocamOff'
import { audioManager } from 'src/consumer/utils/audioManager'
import {
  DeviceType,
  HMSRoomProvider,
  HMSRoomState,
  selectCameraStreamByPeerID,
  selectIsConnectedToRoom,
  selectIsPeerAudioEnabled,
  selectIsPeerVideoEnabled,
  selectLocalAudioTrackID,
  selectLocalPeer,
  selectPeerScreenSharing,
  selectPeers,
  selectRoomState,
  selectScreenShareByPeerID,
  useAVToggle,
  useDevices,
  useHMSActions,
  useHMSStore,
  useScreenShare,
  useVideo,
} from '@100mslive/react-sdk'
import { HUNDRED_MS_RTC_LAYOUTS, normalizeHundredMsRtcLayout } from './hundredMsRtcCompat'
import { normalizeResolvedVideoCallQuality } from '../../routing/videoCallQualityContract'

function getInitials(name) {
  if (typeof name !== 'string' || !name.trim()) {
    return '?'
  }

  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 1) {
    return words[0].slice(0, 1).toUpperCase()
  }

  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
}

function getPeerDisplayName(peer, isLocalPeer = false) {
  if (isLocalPeer) {
    return 'You'
  }

  if (typeof peer?.name === 'string' && peer.name.trim()) {
    return peer.name.trim()
  }

  return 'Guest'
}

function buildJoinConfig(rtcProps) {
  return {
    userName: rtcProps.userName,
    authToken: rtcProps.authToken,
    metaData:
      typeof rtcProps.peerMetadata === 'string'
        ? rtcProps.peerMetadata
        : rtcProps.peerMetadata
          ? JSON.stringify(rtcProps.peerMetadata)
          : undefined,
    rememberDeviceSelection: rtcProps.rememberDeviceSelection !== false,
    autoManageVideo: rtcProps.autoManageVideo !== false,
    autoManageWakeLock: rtcProps.autoManageWakeLock !== false,
    settings: {
      isAudioMuted: rtcProps.enableAudio === false,
      isVideoMuted: rtcProps.enableVideo === false,
      audioInputDeviceId: rtcProps.microphoneDeviceId,
      audioOutputDeviceId: rtcProps.playbackDeviceId,
      videoDeviceId: rtcProps.cameraDeviceId,
    },
  }
}

function buildHundredMsVideoSettings(videoQuality) {
  const publishProfile = normalizeResolvedVideoCallQuality(videoQuality)?.publishProfile

  if (!publishProfile) {
    return null
  }

  return {
    width: publishProfile.width,
    height: publishProfile.height,
    maxFramerate: publishProfile.frameRate,
    maxBitrate: publishProfile.bitrateKbps,
  }
}

function getHundredMsVideoSettingsKey(settings) {
  if (!settings) {
    return ''
  }

  return [
    settings.width,
    settings.height,
    settings.maxFramerate,
    settings.maxBitrate,
  ].join(':')
}

function invokeCallback(callback, ...args) {
  if (typeof callback === 'function') {
    callback(...args)
  }
}

const SILENT_OBSERVER_ROLES = Object.freeze(['auditor'])

function isSilentObserverRoleName(roleName) {
  return (
    typeof roleName === 'string' &&
    SILENT_OBSERVER_ROLES.includes(roleName.trim().toLowerCase())
  )
}

// Audit observers must join without announcing themselves (Agora subscribers
// are naturally invisible; 100ms peers always show up in the roster).
function isSilentObserverPeer(peer) {
  if (isSilentObserverRoleName(peer?.roleName || peer?.role?.name)) {
    return true
  }

  const metadata = peer?.metadata
  if (typeof metadata === 'string' && metadata) {
    try {
      return isSilentObserverRoleName(JSON.parse(metadata)?.role)
    } catch (error) {
      return false
    }
  }

  if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
    return isSilentObserverRoleName(metadata.role)
  }

  return false
}

function buildParticipantSnapshot({
  peer,
  isLocal = false,
  isScreenSharing = false,
  isAudioEnabled = false,
  isVideoEnabled = false,
}) {
  if (!peer?.id) {
    return null
  }

  return {
    peerId: peer.id,
    id: peer.id,
    isLocal,
    name: peer.name,
    roleName: peer.roleName || peer.role?.name || null,
    metadata: peer.metadata,
    audioTrackId: peer.audioTrack || null,
    videoTrackId: peer.videoTrack || null,
    isAudioEnabled,
    isVideoEnabled,
    screenSharing: isScreenSharing,
  }
}

function DefaultHundredMsPlaceholder({ name, isLocalPeer }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.5,
        background:
          isLocalPeer
            ? 'radial-gradient(circle at top, rgba(139, 92, 246, 0.45), rgba(15, 23, 42, 0.95) 62%)'
            : 'radial-gradient(circle at top, rgba(59, 130, 246, 0.4), rgba(15, 23, 42, 0.95) 62%)',
      }}
    >
      <Box
        sx={{
          width: 84,
          height: 84,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255, 255, 255, 0.14)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          color: '#ffffff',
          fontSize: '30px',
          fontWeight: 700,
          backdropFilter: 'blur(10px)',
        }}
      >
        {getInitials(name)}
      </Box>
      <Typography sx={{ color: '#ffffff', fontSize: '14px', fontWeight: 600 }}>
        {name}
      </Typography>
    </Box>
  )
}

function HundredMsVideoTile({ peer, isPrimary = false, isScreenShare = false, CustomVideoPlaceholder }) {
  const videoTrack = useHMSStore(
    isScreenShare ? selectScreenShareByPeerID(peer?.id) : selectCameraStreamByPeerID(peer?.id),
  )
  const isAudioEnabled = useHMSStore(selectIsPeerAudioEnabled(peer?.id))
  const isVideoEnabled = useHMSStore(selectIsPeerVideoEnabled(peer?.id))
  const { videoRef } = useVideo({
    trackId: videoTrack?.id,
    attach: Boolean(videoTrack?.enabled !== false),
  })

  const displayName = getPeerDisplayName(peer, Boolean(peer?.isLocal))
  const PlaceholderComponent = CustomVideoPlaceholder || DefaultHundredMsPlaceholder

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: isPrimary ? '18px' : '14px',
        background: 'linear-gradient(180deg, rgba(15, 23, 42, 1), rgba(2, 6, 23, 1))',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: isPrimary
          ? '0 18px 50px rgba(15, 23, 42, 0.28)'
          : '0 8px 24px rgba(15, 23, 42, 0.18)',
      }}
    >
      {isVideoEnabled && videoTrack ? (
        <video
          ref={videoRef}
          autoPlay
          muted={Boolean(peer?.isLocal)}
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: isScreenShare ? 'contain' : 'cover',
            background: '#020617',
            transform: peer?.isLocal && !isScreenShare ? 'scaleX(-1)' : 'none',
          }}
        />
      ) : (
        <PlaceholderComponent name={displayName} isLocalPeer={Boolean(peer?.isLocal)} peer={peer} />
      )}

      <Box
        sx={{
          position: 'absolute',
          left: 12,
          right: 12,
          bottom: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.25,
            py: 0.75,
            borderRadius: '999px',
            background: 'rgba(15, 23, 42, 0.72)',
            color: '#ffffff',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{displayName}</Typography>
          {isScreenShare && (
            <Typography sx={{ fontSize: '11px', opacity: 0.72 }}>Screen</Typography>
          )}
        </Box>

        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isAudioEnabled ? 'rgba(16, 185, 129, 0.18)' : 'rgba(239, 68, 68, 0.18)',
            color: '#ffffff',
          }}
        >
          {isAudioEnabled ? <MicIcon sx={{ fontSize: 16 }} /> : <MicOffIcon sx={{ fontSize: 16 }} />}
        </Box>
      </Box>
    </Box>
  )
}

function HundredMsRtcRoomSurface({
  rtcProps,
  callbacks = {},
  fallback = null,
  onProviderError,
  renderless = false,
  children = null,
}) {
  const hmsActions = useHMSActions()
  const roomState = useHMSStore(selectRoomState)
  const isConnected = useHMSStore(selectIsConnectedToRoom)
  const localPeer = useHMSStore(selectLocalPeer)
  const peers = useHMSStore(selectPeers) || []
  const peerMediaStateById = useHMSStore((store) => {
    const snapshotPeers = selectPeers(store) || []

    return snapshotPeers.reduce((mediaStateById, peer) => {
      if (!peer?.id) {
        return mediaStateById
      }

      mediaStateById[peer.id] = {
        isAudioEnabled: selectIsPeerAudioEnabled(peer.id)(store),
        isVideoEnabled: selectIsPeerVideoEnabled(peer.id)(store),
      }

      return mediaStateById
    }, {})
  })
  const screenSharePeer = useHMSStore(selectPeerScreenSharing)
  const { isLocalAudioEnabled, isLocalVideoEnabled, toggleAudio, toggleVideo } = useAVToggle(
    (error) => {
      console.error('[HundredMsRtcCompatibilityShell] AV toggle error', error)
    },
  )
  const { updateDevice, selectedDeviceIDs } = useDevices((error) => {
    console.error('[HundredMsRtcCompatibilityShell] Device change error', error)
  })
  const callbacksRef = React.useRef(callbacks)
  callbacksRef.current = callbacks
  const amIScreenSharingRef = React.useRef(false)
  // Stable handler: the SDK routes screen-share failures (including a
  // cancelled browser picker) here instead of rejecting toggleScreenShare(),
  // and an inline handler would change toggleScreenShare's identity every
  // render. Report the unchanged actual state so the session-handle config
  // gets reconciled and the request isn't retried in a loop.
  const handleScreenShareError = React.useCallback((error) => {
    console.error('[HundredMsRtcCompatibilityShell] Screen-share error', error)
    invokeCallback(
      callbacksRef.current?.['screen-share-state-changed'],
      amIScreenSharingRef.current === true,
    )
  }, [])
  const { amIScreenSharing, toggleScreenShare } = useScreenShare(handleScreenShareError)
  amIScreenSharingRef.current = amIScreenSharing === true
  const normalizedVideoQuality = React.useMemo(
    () => normalizeResolvedVideoCallQuality(rtcProps?.videoQuality),
    [rtcProps?.videoQuality],
  )
  const desiredVideoSettings = React.useMemo(
    () => buildHundredMsVideoSettings(normalizedVideoQuality),
    [normalizedVideoQuality],
  )

  const previousRemotePeersRef = React.useRef(new Map())
  const hasSyncedAudioRef = React.useRef(false)
  const hasSyncedVideoRef = React.useRef(false)
  const lastAppliedVideoSettingsKeyRef = React.useRef('')
  const joinKeyRef = React.useRef(null)
  const previousRoomStateRef = React.useRef(roomState)

  React.useEffect(() => {
    const authToken = rtcProps?.authToken
    const userName = rtcProps?.userName

    if (!authToken || !userName) {
      if (typeof onProviderError === 'function') {
        onProviderError(new Error('Missing 100ms auth token or username'))
      }
      return undefined
    }

    if (
      roomState !== HMSRoomState.Disconnected &&
      roomState !== HMSRoomState.Failed
    ) {
      return undefined
    }

    const joinKey = `${authToken}::${userName}`
    if (joinKeyRef.current === joinKey) {
      return undefined
    }

    joinKeyRef.current = joinKey
    let cancelled = false

    hmsActions.join(buildJoinConfig(rtcProps)).catch((error) => {
      console.error('[HundredMsRtcCompatibilityShell] Join failed', error)
      joinKeyRef.current = null

      if (!cancelled && typeof onProviderError === 'function') {
        onProviderError(error)
      }
    })

    return () => {
      cancelled = true
    }
  }, [
    hmsActions,
    onProviderError,
    roomState,
    rtcProps,
    rtcProps?.authToken,
    rtcProps?.cameraDeviceId,
    rtcProps?.enableAudio,
    rtcProps?.enableVideo,
    rtcProps?.microphoneDeviceId,
    rtcProps?.peerMetadata,
    rtcProps?.playbackDeviceId,
    rtcProps?.userName,
  ])

  React.useEffect(() => {
    return () => {
      hmsActions.leave().catch(() => {})
    }
  }, [hmsActions])

  React.useEffect(() => {
    if (!isConnected || !rtcProps?.microphoneDeviceId) {
      return
    }

    if (selectedDeviceIDs?.audioInput === rtcProps.microphoneDeviceId) {
      return
    }

    updateDevice({
      deviceType: DeviceType.audioInput,
      deviceId: rtcProps.microphoneDeviceId,
    }).catch(() => {})
  }, [isConnected, rtcProps?.microphoneDeviceId, selectedDeviceIDs?.audioInput, updateDevice])

  React.useEffect(() => {
    if (!isConnected || !rtcProps?.cameraDeviceId) {
      return
    }

    if (selectedDeviceIDs?.videoInput === rtcProps.cameraDeviceId) {
      return
    }

    updateDevice({
      deviceType: DeviceType.videoInput,
      deviceId: rtcProps.cameraDeviceId,
    }).catch(() => {})
  }, [isConnected, rtcProps?.cameraDeviceId, selectedDeviceIDs?.videoInput, updateDevice])

  React.useEffect(() => {
    if (!isConnected || !rtcProps?.playbackDeviceId) {
      return
    }

    if (selectedDeviceIDs?.audioOutput === rtcProps.playbackDeviceId) {
      return
    }

    updateDevice({
      deviceType: DeviceType.audioOutput,
      deviceId: rtcProps.playbackDeviceId,
    }).catch(() => {})
  }, [isConnected, rtcProps?.playbackDeviceId, selectedDeviceIDs?.audioOutput, updateDevice])

  React.useEffect(() => {
    if (!isConnected || !desiredVideoSettings) {
      return
    }

    const nextSettingsKey = getHundredMsVideoSettingsKey(desiredVideoSettings)
    if (!nextSettingsKey || lastAppliedVideoSettingsKeyRef.current === nextSettingsKey) {
      return
    }

    hmsActions.setVideoSettings(desiredVideoSettings)
      .then(() => {
        lastAppliedVideoSettingsKeyRef.current = nextSettingsKey
      })
      .catch((error) => {
        console.warn(
          '[HundredMsRtcCompatibilityShell] Failed to apply video settings',
          error,
        )
      })
  }, [desiredVideoSettings, hmsActions, isConnected])

  React.useEffect(() => {
    if (!isConnected || typeof rtcProps?.enableAudio !== 'boolean') {
      return
    }

    if (!hasSyncedAudioRef.current) {
      hasSyncedAudioRef.current = true
      return
    }

    if (rtcProps.enableAudio === isLocalAudioEnabled) {
      return
    }

    hmsActions.setLocalAudioEnabled(rtcProps.enableAudio).catch(() => {})
  }, [hmsActions, isConnected, isLocalAudioEnabled, rtcProps?.enableAudio])

  React.useEffect(() => {
    if (!isConnected || typeof rtcProps?.enableVideo !== 'boolean') {
      return
    }

    if (!hasSyncedVideoRef.current) {
      hasSyncedVideoRef.current = true
      return
    }

    if (rtcProps.enableVideo === isLocalVideoEnabled) {
      return
    }

    hmsActions.setLocalVideoEnabled(rtcProps.enableVideo).catch(() => {})
  }, [hmsActions, isConnected, isLocalVideoEnabled, rtcProps?.enableVideo])

  // Edge-triggered desired-state sync: act only when enableScreensharing
  // CHANGES, never on effect re-runs, so a cancelled/blocked picker is not
  // re-opened. Starting a share needs a user gesture anyway, so the first
  // observed value is treated as a baseline instead of auto-starting.
  const lastSyncedScreenShareDesireRef = React.useRef(null)
  React.useEffect(() => {
    if (!isConnected || typeof rtcProps?.enableScreensharing !== 'boolean') {
      return
    }

    const desiredScreenShare = rtcProps.enableScreensharing
    if (lastSyncedScreenShareDesireRef.current === desiredScreenShare) {
      return
    }

    const isBaselineRun = lastSyncedScreenShareDesireRef.current === null
    lastSyncedScreenShareDesireRef.current = desiredScreenShare

    if (isBaselineRun || desiredScreenShare === amIScreenSharing) {
      return
    }

    toggleScreenShare?.().catch(() => {})
  }, [amIScreenSharing, isConnected, rtcProps?.enableScreensharing, toggleScreenShare])

  // Report actual screen-share transitions (started, stopped via the browser
  // "Stop sharing" bar, track ended) so the shared shell and session-handle
  // config stay aligned with reality.
  const previousScreenShareActiveRef = React.useRef()
  React.useEffect(() => {
    if (previousScreenShareActiveRef.current === undefined) {
      previousScreenShareActiveRef.current = amIScreenSharing === true
      return
    }

    if (previousScreenShareActiveRef.current === (amIScreenSharing === true)) {
      return
    }

    previousScreenShareActiveRef.current = amIScreenSharing === true
    invokeCallback(callbacksRef.current?.['screen-share-state-changed'], amIScreenSharing === true)
  }, [amIScreenSharing])

  // 100ms mutes audio by only disabling the native track, which keeps the
  // microphone captured (OS/browser mic indicator stays on). Stop the native
  // track while muted so the device is actually released — unmuting is safe
  // because HMSLocalAudioTrack.setEnabled(true) re-acquires an ended track
  // (shouldReacquireTrack -> replaceTrackWith). The SDK may silently
  // re-acquire on tab visibility changes, so re-release then too.
  const localAudioTrackId = useHMSStore(selectLocalAudioTrackID)
  React.useEffect(() => {
    if (!isConnected || isLocalAudioEnabled || !localAudioTrackId) {
      return undefined
    }

    const releaseMicrophone = () => {
      try {
        const nativeTrack = hmsActions.getNativeTrackById?.(localAudioTrackId)
        if (nativeTrack && nativeTrack.readyState === 'live' && !nativeTrack.enabled) {
          nativeTrack.stop()
        }
      } catch (error) {
        console.warn('[HundredMsRtcCompatibilityShell] Failed to release muted microphone', error)
      }
    }

    // Small delay so a rapid mute/unmute does not churn the device.
    const releaseTimer = setTimeout(releaseMicrophone, 600)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setTimeout(releaseMicrophone, 600)
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      clearTimeout(releaseTimer)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [hmsActions, isConnected, isLocalAudioEnabled, localAudioTrackId])

  const previousAudioEnabledRef = React.useRef()
  React.useEffect(() => {
    if (!isConnected) {
      previousAudioEnabledRef.current = isLocalAudioEnabled
      return
    }

    if (previousAudioEnabledRef.current === undefined) {
      previousAudioEnabledRef.current = isLocalAudioEnabled
      return
    }

    if (previousAudioEnabledRef.current !== isLocalAudioEnabled) {
      invokeCallback(callbacks.onAudioStateChange, isLocalAudioEnabled)
    }

    previousAudioEnabledRef.current = isLocalAudioEnabled
  }, [callbacks.onAudioStateChange, isConnected, isLocalAudioEnabled])

  const previousVideoEnabledRef = React.useRef()
  React.useEffect(() => {
    if (!isConnected) {
      previousVideoEnabledRef.current = isLocalVideoEnabled
      return
    }

    if (previousVideoEnabledRef.current === undefined) {
      previousVideoEnabledRef.current = isLocalVideoEnabled
      return
    }

    if (previousVideoEnabledRef.current !== isLocalVideoEnabled) {
      invokeCallback(callbacks.onCameraStateChange, isLocalVideoEnabled)
    }

    previousVideoEnabledRef.current = isLocalVideoEnabled
  }, [callbacks.onCameraStateChange, isConnected, isLocalVideoEnabled])

  React.useEffect(() => {
    if (previousRoomStateRef.current === roomState) {
      return
    }

    invokeCallback(
      callbacks['connection-state-change'],
      roomState,
      previousRoomStateRef.current,
      null,
    )
    previousRoomStateRef.current = roomState
  }, [callbacks, roomState])

  React.useEffect(() => {
    const remotePeers = peers.filter((peer) => !peer?.isLocal)
    const previousPeers = previousRemotePeersRef.current
    const nextPeers = new Map(remotePeers.map((peer) => [peer.id, peer]))

    nextPeers.forEach((peer, peerId) => {
      if (!previousPeers.has(peerId) && !isSilentObserverPeer(peer)) {
        audioManager.playJoinSound()
        invokeCallback(callbacks.onUserJoin, {
          uid: peer.id,
          peer,
        })
      }
    })

    previousPeers.forEach((peer, peerId) => {
      if (!nextPeers.has(peerId) && !isSilentObserverPeer(peer)) {
        audioManager.playLeaveSound()
        invokeCallback(callbacks.onUserLeave, {
          uid: peer.id,
          peer,
        })
      }
    })

    previousRemotePeersRef.current = nextPeers
  }, [callbacks.onUserJoin, callbacks.onUserLeave, peers])

  React.useEffect(() => {
    const participantSnapshot = peers
      .map((peer) =>
        buildParticipantSnapshot({
          peer,
          isLocal: Boolean(peer?.isLocal),
          isScreenSharing: screenSharePeer?.id === peer?.id,
          isAudioEnabled:
            peerMediaStateById?.[peer?.id]?.isAudioEnabled ?? Boolean(peer?.audioTrack),
          isVideoEnabled:
            peerMediaStateById?.[peer?.id]?.isVideoEnabled ?? Boolean(peer?.videoTrack),
        }),
      )
      .filter(Boolean)

    invokeCallback(callbacks['participants-updated'], {
      participants: participantSnapshot,
    })
  }, [callbacks, peerMediaStateById, peers, screenSharePeer?.id])

  const layout = normalizeHundredMsRtcLayout(rtcProps?.layout)
  const remotePeers = React.useMemo(
    () => peers.filter((peer) => !peer?.isLocal),
    [peers],
  )
  const tilePeers = React.useMemo(() => {
    const orderedPeers = []

    if (screenSharePeer && screenSharePeer.id) {
      orderedPeers.push({
        key: `screen-${screenSharePeer.id}`,
        peer: screenSharePeer,
        isScreenShare: true,
      })
    }

    remotePeers.forEach((peer) => {
      orderedPeers.push({
        key: `peer-${peer.id}`,
        peer,
        isScreenShare: false,
      })
    })

    if (localPeer) {
      orderedPeers.push({
        key: `peer-${localPeer.id || 'local'}`,
        peer: {
          ...localPeer,
          isLocal: true,
        },
        isScreenShare: false,
      })
    }

    return orderedPeers
  }, [localPeer, remotePeers, screenSharePeer])

  const primaryTile = tilePeers[0]
  const secondaryTiles = tilePeers.slice(1)

  if (renderless) {
    return children
  }

  if (
    roomState === HMSRoomState.Connecting ||
    roomState === HMSRoomState.Reconnecting ||
    (!isConnected && roomState !== HMSRoomState.Disconnected && roomState !== HMSRoomState.Failed)
  ) {
    return fallback || (
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          background: 'linear-gradient(135deg, #0f172a 0%, #111827 100%)',
          color: '#ffffff',
        }}
      >
        <CircularProgress size={40} sx={{ color: '#38bdf8' }} />
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Connecting to video call...</Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #020617 0%, #0f172a 100%)',
      }}
    >
      {layout === HUNDRED_MS_RTC_LAYOUTS.pin && primaryTile ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: secondaryTiles.length > 0 ? 'minmax(0, 1fr) 280px' : 'minmax(0, 1fr)',
            gap: 1.5,
            height: '100%',
            p: 1.5,
          }}
        >
          <Box sx={{ minHeight: 0 }}>
            <HundredMsVideoTile
              peer={primaryTile.peer}
              isPrimary
              isScreenShare={primaryTile.isScreenShare}
              CustomVideoPlaceholder={rtcProps?.CustomVideoPlaceholder}
            />
          </Box>

          {secondaryTiles.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridAutoRows: 'minmax(140px, 1fr)',
                gap: 1.5,
                minHeight: 0,
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              {secondaryTiles.map((tile) => (
                <HundredMsVideoTile
                  key={tile.key}
                  peer={tile.peer}
                  isScreenShare={tile.isScreenShare}
                  CustomVideoPlaceholder={rtcProps?.CustomVideoPlaceholder}
                />
              ))}
            </Box>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, minmax(0, 1fr))',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 1.5,
            height: '100%',
            p: 1.5,
          }}
        >
          {tilePeers.map((tile) => (
            <HundredMsVideoTile
              key={tile.key}
              peer={tile.peer}
              isScreenShare={tile.isScreenShare}
              CustomVideoPlaceholder={rtcProps?.CustomVideoPlaceholder}
            />
          ))}
        </Box>
      )}

      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 20,
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 1,
          borderRadius: '999px',
          background: 'rgba(15, 23, 42, 0.65)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(14px)',
          zIndex: 2,
        }}
      >
        <IconButton
          onClick={() => toggleAudio?.()}
          sx={{
            color: '#ffffff',
            background: isLocalAudioEnabled ? 'rgba(30, 41, 59, 0.85)' : 'rgba(220, 38, 38, 0.82)',
            '&:hover': {
              background: isLocalAudioEnabled ? 'rgba(51, 65, 85, 0.95)' : 'rgba(185, 28, 28, 0.92)',
            },
          }}
        >
          {isLocalAudioEnabled ? <MicIcon /> : <MicOffIcon />}
        </IconButton>

        <IconButton
          onClick={() => toggleVideo?.()}
          sx={{
            color: '#ffffff',
            background: isLocalVideoEnabled ? 'rgba(30, 41, 59, 0.85)' : 'rgba(220, 38, 38, 0.82)',
            '&:hover': {
              background: isLocalVideoEnabled ? 'rgba(51, 65, 85, 0.95)' : 'rgba(185, 28, 28, 0.92)',
            },
          }}
        >
          {isLocalVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
        </IconButton>

        {(rtcProps?.screenshareAllowed ?? rtcProps?.enableScreensharing) && (
          <IconButton
            onClick={() => toggleScreenShare?.()}
            sx={{
              color: '#ffffff',
              background: amIScreenSharing
                ? 'rgba(2, 132, 199, 0.82)'
                : 'rgba(30, 41, 59, 0.85)',
              '&:hover': {
                background: amIScreenSharing
                  ? 'rgba(3, 105, 161, 0.92)'
                  : 'rgba(51, 65, 85, 0.95)',
              },
            }}
          >
            {amIScreenSharing ? <StopScreenShareIcon /> : <PresentToAllIcon />}
          </IconButton>
        )}

        <IconButton
          onClick={() => {
            hmsActions
              .leave()
              .catch(() => {})
              .finally(() => {
                invokeCallback(callbacks.onLeave)
              })
          }}
          sx={{
            color: '#ffffff',
            background: 'rgba(220, 38, 38, 0.92)',
            '&:hover': {
              background: 'rgba(185, 28, 28, 0.98)',
            },
          }}
        >
          <CallEndIcon />
        </IconButton>
      </Box>
    </Box>
  )
}

export function HundredMsRtcCompatibilityShell(props) {
  return (
    <HMSRoomProvider leaveOnUnload={false}>
      <HundredMsRtcRoomSurface {...props} />
    </HMSRoomProvider>
  )
}

export default HundredMsRtcCompatibilityShell
